import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Checkbox } from "./Choice.tsx";
import { ChevronDownIcon } from "./Icons.tsx";
import { EmptyState } from "./States.tsx";

/**
 * The register, at desktop width.
 *
 * A plain `<table>` with no library behind it: columns are declared, rows are data, and the three
 * behaviours an owner list needs (sort by a column, click a row, tick a few rows) are done here
 * once. Sorting is client side and on purpose: every owner list in the product is a few hundred
 * rows at most, and a sort that round-trips to the API is a sort that feels broken on a pool deck.
 *
 * Keyboard: a sortable header is a button; a clickable row is focusable and opens on Enter or
 * Space; a selection checkbox is a real checkbox with its own label. The header is sticky only
 * at 1024px and up. The sideways-scroll frame below is the sticky container, so on a phone an
 * offset of the top bar's height pushed the header down over the first row.
 *
 * The table scrolls sideways inside its own frame on a narrow screen, so a wide register never
 * makes the page itself scroll horizontally at 390px.
 */

export type SortDirection = "asc" | "desc";

export type DataTableColumn<Row> = {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  /** Set to make the column sortable. Strings sort case-insensitively, nulls last. */
  sortValue?: (row: Row) => string | number | null;
  align?: "start" | "end";
  /** A CSS width for the column, when the content does not size it well on its own. */
  width?: string;
  /** Hidden below the `md` breakpoint, for a column a phone can live without. */
  hideOnPhone?: boolean;
};

export type DataTableProps<Row> = {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** A short name for the row, used to label its checkbox and its open action. */
  rowLabel?: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** With `onRowClick`: false for a row that has nothing to open (it is then not focusable). */
  canOpenRow?: (row: Row) => boolean;
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
  };
  isLoading?: boolean;
  empty: { title: string; message: string; action?: ReactNode };
  defaultSort?: { id: string; direction: SortDirection };
  /**
   * Set while a search ranks the rows best first: the rows keep their order, and `defaultSort` waits
   * until the search ends. A column sort the person picks still applies.
   */
  ranked?: boolean;
  /** "3 members", "12 payments". Defaults to "n rows". */
  countLabel?: (count: number) => string;
  caption?: string;
  /**
   * `compact` uses 12px side padding instead of 16px. For a register with many columns (the admin
   * businesses table has nine) that has to fit beside the sidebar in a 1280px window.
   */
  density?: "comfortable" | "compact";
  /**
   * A row the person acted on stays in the list and says so by its fill: `done` is a light green
   * (saved, linked, kept), `aside` a light grey (skipped, later, archived). The words in the row say
   * the same, so the colour is never the only sign. The WhatsApp sync tool uses it.
   */
  rowTone?: (row: Row) => RowTone | undefined;
};

export type RowTone = "done" | "aside";

const ROW_TONES: Record<RowTone, string> = {
  done: "bg-confirmed-surface/55",
  aside: "bg-sunken/70",
};

type SortValue = string | number | null;

/** Nulls sort last in both directions; only the values themselves flip with the direction. */
function compare(a: SortValue, b: SortValue, sign: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * sign;
  return (
    String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true }) * sign
  );
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowLabel,
  onRowClick,
  canOpenRow,
  selection,
  isLoading = false,
  empty,
  defaultSort,
  ranked = false,
  countLabel,
  caption,
  density = "comfortable",
  rowTone,
}: DataTableProps<Row>) {
  const [chosen, setSort] = useState<{ id: string; direction: SortDirection } | null>(
    defaultSort ?? null,
  );
  /** True once the person picks a column sort. Then a search no longer replaces it. */
  const [picked, setPicked] = useState(false);
  const sort = ranked && !picked ? null : chosen;

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((candidate) => candidate.id === sort.id);
    const value = column?.sortValue;
    if (!value) return rows;
    const sign = sort.direction === "asc" ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compare(value(a.row), value(b.row), sign) || a.index - b.index)
      .map((entry) => entry.row);
  }, [rows, sort, columns]);

  function toggleSort(id: string) {
    setPicked(true);
    // The first click while a search ranks the rows starts that column at ascending.
    setSort((current) =>
      sort && current?.id === id
        ? { id, direction: current.direction === "asc" ? "desc" : "asc" }
        : { id, direction: "asc" },
    );
  }

  const allKeys = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allSelected = selection !== undefined && allKeys.length > 0 && allKeys.every((key) => selection.selected.has(key));
  const someSelected = selection !== undefined && !allSelected && allKeys.some((key) => selection.selected.has(key));

  function toggleAll() {
    if (!selection) return;
    selection.onChange(allSelected ? new Set() : new Set(allKeys));
  }

  function toggleOne(key: string) {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange(next);
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: Row) {
    if (!onRowClick || canOpenRow?.(row) === false) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick(row);
    }
  }

  const columnCount = columns.length + (selection ? 1 : 0);
  const padX = density === "compact" ? "px-3" : "px-4";
  const cellPad = `${padX} py-3`;
  const align = (column: DataTableColumn<Row>) =>
    column.align === "end" ? "text-right" : "text-left";
  const phone = (column: DataTableColumn<Row>) => (column.hideOnPhone ? "hidden md:table-cell" : "");

  return (
    <div className="overflow-hidden rounded-card border border-line bg-paper shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[14px]" aria-busy={isLoading || undefined}>
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {selection ? (
                <th
                  scope="col"
                  className="z-10 w-10 lg:sticky lg:top-(--sticky-top) border-b border-line bg-paper px-4 py-2.5"
                >
                  <Checkbox
                    hideLabel
                    label="Select all rows"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => toggleAll()}
                    disabled={rows.length === 0}
                  />
                </th>
              ) : null}
              {columns.map((column) => {
                const active = sort?.id === column.id;
                const ariaSort = active
                  ? sort.direction === "asc"
                    ? "ascending"
                    : "descending"
                  : undefined;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={column.sortValue ? ariaSort ?? "none" : undefined}
                    style={column.width ? { width: column.width } : undefined}
                    className={[
                      `z-10 border-b border-line bg-paper ${padX} py-2.5 lg:sticky lg:top-(--sticky-top)`,
                      "text-[11px] font-semibold tracking-[0.08em] text-ink-3 uppercase",
                      align(column),
                      phone(column),
                    ].join(" ")}
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.id)}
                        className={[
                          "inline-flex min-h-6 items-center gap-1 rounded",
                          active ? "text-ink" : "hover:text-ink-2",
                        ].join(" ")}
                      >
                        {column.header}
                        <ChevronDownIcon
                          size={14}
                          className={[
                            "transition-transform duration-150",
                            active ? "" : "opacity-40",
                            active && sort.direction === "desc" ? "rotate-180" : "",
                          ].join(" ")}
                        />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount} className={cellPad}>
                  <span className="sr-only">Loading</span>
                  <span className="flex flex-col gap-4 py-1" aria-hidden="true">
                    {["w-2/3", "w-1/2", "w-3/5", "w-2/5"].map((width) => (
                      <span key={width} className={`block h-3.5 ${width} animate-pulse rounded bg-sunken`} />
                    ))}
                  </span>
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-4 py-4">
                  <EmptyState compact title={empty.title} message={empty.message} action={empty.action} />
                </td>
              </tr>
            ) : (
              sorted.map((row) => {
                const key = rowKey(row);
                const selected = selection?.selected.has(key) ?? false;
                const name = rowLabel?.(row);
                const tone = rowTone?.(row);
                const opens = Boolean(onRowClick) && canOpenRow?.(row) !== false;
                return (
                  <tr
                    key={key}
                    tabIndex={opens ? 0 : undefined}
                    aria-label={opens && name ? name : undefined}
                    aria-selected={selection ? selected : undefined}
                    onClick={opens ? () => onRowClick?.(row) : undefined}
                    onKeyDown={(event) => onRowKeyDown(event, row)}
                    className={[
                      "border-b border-line last:border-b-0",
                      opens ? "cursor-pointer hover:bg-surface focus-visible:bg-surface" : "",
                      selected ? "bg-accent-surface/60" : tone ? ROW_TONES[tone] : "",
                    ].join(" ")}
                  >
                    {selection ? (
                      <td
                        className={cellPad}
                        // A click or a key on the box selects the row; it must not also open it.
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          hideLabel
                          label={name ? `Select ${name}` : "Select row"}
                          checked={selected}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                    ) : null}
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        // A name or a note can be in Arabic. `dir="auto"` reads the first strong letter, so
                        // an Arabic name runs right to left inside its cell, while `text-left` keeps the
                        // column lined up at the left like the other rows. A cell of English, digits or a
                        // date stays left to right. A number column (`align: "end"`) keeps the page
                        // direction.
                        dir={column.align === "end" ? undefined : "auto"}
                        className={`${cellPad} align-middle text-ink ${align(column)} ${phone(column)}`}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-line bg-surface/60 px-4 py-2 text-[12px] text-ink-2">
        <span>{isLoading ? "Loading" : (countLabel?.(rows.length) ?? `${rows.length} ${rows.length === 1 ? "row" : "rows"}`)}</span>
        {selection && selection.selected.size > 0 ? (
          <span className="font-semibold text-ink">{selection.selected.size} selected</span>
        ) : null}
      </div>
    </div>
  );
}
